import { useState } from 'react';
import {
	MouseSensor,
	TouchSensor,
	KeyboardSensor,
	useSensor,
	useSensors,
	type DragEndEvent,
	type DragStartEvent
} from '@dnd-kit/core';
import type { CalendarEventModel } from './types';

export interface UseCalendarDndOptions<T extends CalendarEventModel> {
	events: T[];
	onDragStopped?: (eventId: string, newStartTime: Date) => void;
	isDragAndDropValid?: (eventId: string, newStartTime: Date) => boolean;
}

export interface UseCalendarDndResult<T extends CalendarEventModel> {
	sensors: ReturnType<typeof useSensors>;
	activeEvent: T | null;
	handleDragStart: (ev: DragStartEvent) => void;
	handleDragEnd: (ev: DragEndEvent) => void;
	handleDragCancel: () => void;
}

export function combineDateWithTimeOfDay(targetDate: Date, sourceDateTime: Date): Date {
	const result = new Date(targetDate);
	result.setHours(sourceDateTime.getHours(), sourceDateTime.getMinutes(), sourceDateTime.getSeconds(), sourceDateTime.getMilliseconds());
	return result;
}

export function useCalendarDnd<T extends CalendarEventModel>({
	events,
	onDragStopped,
	isDragAndDropValid
}: UseCalendarDndOptions<T>): UseCalendarDndResult<T> {
	const [activeEventId, setActiveEventId] = useState<string | null>(null);

	const sensors = useSensors(
		useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
		useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } }),
		useSensor(KeyboardSensor)
	);

	const activeEvent = activeEventId ? events.find(e => e.id === activeEventId) ?? null : null;

	const handleDragStart = (ev: DragStartEvent) => {
		setActiveEventId(String(ev.active.id));
	};

	const handleDragEnd = (ev: DragEndEvent) => {
		setActiveEventId(null);
		const { active, over } = ev;
		if (!over) return;
		const targetDate = over.data.current?.date as Date | undefined;
		if (!targetDate) return;
		const eventId = String(active.id);
		const original = events.find(e => e.id === eventId);
		if (!original) return;
		const targetStartTime = over.data.current?.startTime as Date | undefined;
		const newStartTime = targetStartTime ?? combineDateWithTimeOfDay(targetDate, original.startTime);
		if (isDragAndDropValid && !isDragAndDropValid(eventId, newStartTime)) return;
		onDragStopped?.(eventId, newStartTime);
	};

	const handleDragCancel = () => setActiveEventId(null);

	return { sensors, activeEvent, handleDragStart, handleDragEnd, handleDragCancel };
}
